/**
 * @fileoverview Chart Mode Component
 * Full-screen multi-signal analysis workspace.
 * 
 * Features:
 * - Plot up to 4 signals on a single chart
 * - Collapsible sidebar with signal picker
 * - Display mode toggle (normalized, z-score, actual)
 * - Time range controls
 * - Historical events overlay
 * - AI narrative
 * - Export functionality
 * 
 * @module charts/components/ChartMode
 */

import { el } from '../../shared/components/simpleRender.js';
import { ChartModeSidebar, updateSidebarSignals } from './ChartModeSidebar.js';
import { fetchSignalHistory, fetchSignal, ALL_SIGNAL_IDS } from '../../api/capitulators.js';
import { getSignalIntelligence } from '../../signals/data/signalIntelligence.js';
import { generateNarrative } from '../../signals/data/narrativeGenerator.js';
import { createExportDropdown, exportPNG, exportCSV, exportJSON, exportPDF } from '../../signals/data/exportKit.js';

// ============================================================================
// State
// ============================================================================

let chartModeState = {
  signals: [],        // Array of signal IDs (max 4)
  signalData: {},     // Cached signal metadata keyed by ID
  historyData: {},    // Cached history data keyed by ID
  range: '90d',
  mode: 'normalized', // normalized | zscore | actual
  events: true,
  loading: false,
  error: null,
};

let chartInstance = null;
let containerRef = null;

// ============================================================================
// Constants
// ============================================================================

const TIME_RANGES = [
  { id: '30d', label: '30D', days: 30 },
  { id: '90d', label: '90D', days: 90 },
  { id: '1y', label: '1Y', days: 365 },
  { id: '5y', label: '5Y', days: 1825 },
  { id: 'max', label: 'MAX', days: 3650 },
];

const DISPLAY_MODES = [
  { id: 'normalized', label: 'Normalized', desc: '0-100 scale' },
  { id: 'zscore', label: 'Z-Score', desc: 'Std deviations' },
  { id: 'actual', label: 'Actual', desc: 'Real values' },
];

const SERIES_COLORS = [
  { stroke: '#97B8A0', name: 'Fog Green' },      // Primary
  { stroke: '#D4A85F', name: 'Lantern Gold' },   // Secondary
  { stroke: '#00EEE5', name: 'Liquidity Cyan' }, // Tertiary
  { stroke: '#B2332A', name: 'Rust Red' },       // Quaternary
];

const SERIES_STYLES = [
  { dash: '', width: 2.5 },    // Solid, thick
  { dash: '', width: 2 },      // Solid, medium
  { dash: '6,3', width: 2 },   // Dashed
  { dash: '2,2', width: 2 },   // Dotted
];

// ============================================================================
// URL State Management
// ============================================================================

const URL_DEFAULTS = {
  range: '90d',
  mode: 'normalized',
  events: true,
};

/**
 * Update URL with current chart state (without triggering navigation)
 * @param {Object} state - Current chart state
 */
function updateChartURL(state) {
  const params = new URLSearchParams();
  
  if (state.signals && state.signals.length > 0) {
    params.set('signals', state.signals.join(','));
  }
  if (state.range && state.range !== URL_DEFAULTS.range) {
    params.set('range', state.range);
  }
  if (state.mode && state.mode !== URL_DEFAULTS.mode) {
    params.set('mode', state.mode);
  }
  if (state.events === false) {
    params.set('events', 'off');
  }
  
  const queryString = params.toString();
  const newHash = queryString ? `#/charts?${queryString}` : '#/charts';
  
  // Update URL without triggering hashchange
  if (window.location.hash !== newHash) {
    history.replaceState(null, '', newHash);
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

function formatValue(value, signalId) {
  if (value == null) return '—';
  if (signalId === 'NET_LIQUIDITY') return `$${(value / 1e6).toFixed(2)}T`;
  if (signalId === 'LIQUIDITY_REGIME') return String(value).toUpperCase();
  if (Math.abs(value) >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (Math.abs(value) < 0.01) return value.toFixed(4);
  return value.toFixed(2);
}

function toChartData(historyData) {
  if (!historyData || !Array.isArray(historyData)) return [];
  return historyData
    .filter(d => d.value != null)
    .map(d => {
      let timestamp;
      if (typeof d.time === 'number') timestamp = d.time;
      else if (typeof d.date === 'number') timestamp = d.date;
      else if (typeof d.date === 'string') timestamp = Math.floor(new Date(d.date).getTime() / 1000);
      else if (typeof d.time === 'string') timestamp = Math.floor(new Date(d.time).getTime() / 1000);
      else return null;
      return { time: timestamp, value: d.value };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);
}

/**
 * Normalize data to 0-100 scale based on min/max
 */
function normalizeData(data) {
  if (!data || data.length < 2) return data;
  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  if (range === 0) return data.map(d => ({ ...d, value: 50 }));
  return data.map(d => ({
    ...d,
    value: ((d.value - min) / range) * 100,
    originalValue: d.value,
  }));
}

/**
 * Convert data to z-scores
 */
function toZScores(data) {
  if (!data || data.length < 2) return data;
  const values = data.map(d => d.value);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);
  if (stdDev === 0) return data.map(d => ({ ...d, value: 0 }));
  return data.map(d => ({
    ...d,
    value: (d.value - mean) / stdDev,
    originalValue: d.value,
  }));
}

// ============================================================================
// Chart Rendering
// ============================================================================

/**
 * Render multi-series SVG chart
 */
function renderChart(container, seriesData, options = {}) {
  const { mode = 'normalized', showEvents = true } = options;
  
  container.innerHTML = '';
  
  if (!seriesData || seriesData.length === 0) {
    container.innerHTML = '<div class="chart-mode__empty">Select a signal to begin</div>';
    return null;
  }
  
  // Check if all series have data
  const validSeries = seriesData.filter(s => s.data && s.data.length > 0);
  if (validSeries.length === 0) {
    container.innerHTML = '<div class="chart-mode__empty">No data available</div>';
    return null;
  }
  
  // Chart dimensions
  const width = container.clientWidth || 800;
  const height = container.clientHeight || 300;
  const padding = { top: 20, right: 60, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Find global time range across all series
  let minTime = Infinity, maxTime = -Infinity;
  validSeries.forEach(s => {
    s.data.forEach(d => {
      if (d.time < minTime) minTime = d.time;
      if (d.time > maxTime) maxTime = d.time;
    });
  });
  
  // Process data based on display mode
  const processedSeries = validSeries.map(s => {
    let processedData;
    switch (mode) {
      case 'zscore':
        processedData = toZScores(s.data);
        break;
      case 'normalized':
        processedData = normalizeData(s.data);
        break;
      default:
        processedData = s.data.map(d => ({ ...d, originalValue: d.value }));
    }
    return { ...s, processedData };
  });
  
  // Find value range
  let minVal = Infinity, maxVal = -Infinity;
  processedSeries.forEach(s => {
    s.processedData.forEach(d => {
      if (d.value < minVal) minVal = d.value;
      if (d.value > maxVal) maxVal = d.value;
    });
  });
  
  // Add padding to value range
  const valRange = maxVal - minVal;
  minVal -= valRange * 0.05;
  maxVal += valRange * 0.05;
  
  // Scale functions
  const xScale = (time) => padding.left + ((time - minTime) / (maxTime - minTime)) * chartWidth;
  const yScale = (value) => padding.top + chartHeight - ((value - minVal) / (maxVal - minVal)) * chartHeight;
  
  // Build SVG
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('class', 'chart-mode__svg');
  
  // Background
  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bg.setAttribute('width', width);
  bg.setAttribute('height', height);
  bg.setAttribute('fill', '#12181D');
  svg.appendChild(bg);
  
  // Grid lines
  const gridGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  gridGroup.setAttribute('class', 'chart-grid');
  
  // Horizontal grid lines (5 lines)
  for (let i = 0; i <= 4; i++) {
    const y = padding.top + (chartHeight / 4) * i;
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', padding.left);
    line.setAttribute('y1', y);
    line.setAttribute('x2', width - padding.right);
    line.setAttribute('y2', y);
    line.setAttribute('stroke', 'rgba(151, 184, 160, 0.1)');
    line.setAttribute('stroke-width', '1');
    gridGroup.appendChild(line);
    
    // Y-axis labels
    const val = maxVal - (i / 4) * (maxVal - minVal);
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', padding.left - 8);
    label.setAttribute('y', y + 4);
    label.setAttribute('text-anchor', 'end');
    label.setAttribute('fill', 'rgba(151, 184, 160, 0.6)');
    label.setAttribute('font-size', '10');
    label.setAttribute('font-family', 'monospace');
    
    let labelText;
    if (mode === 'normalized') {
      labelText = val.toFixed(0);
    } else if (mode === 'zscore') {
      labelText = val.toFixed(1) + 'σ';
    } else {
      labelText = formatValue(val, processedSeries[0]?.signalId);
    }
    label.textContent = labelText;
    gridGroup.appendChild(label);
  }
  
  svg.appendChild(gridGroup);
  
  // X-axis labels
  const xAxisGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  xAxisGroup.setAttribute('class', 'chart-xaxis');
  
  const labelCount = 5;
  for (let i = 0; i <= labelCount; i++) {
    const time = minTime + (i / labelCount) * (maxTime - minTime);
    const x = xScale(time);
    const date = new Date(time * 1000);
    
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', x);
    label.setAttribute('y', height - padding.bottom + 20);
    label.setAttribute('text-anchor', 'middle');
    label.setAttribute('fill', 'rgba(151, 184, 160, 0.6)');
    label.setAttribute('font-size', '10');
    label.setAttribute('font-family', 'monospace');
    label.textContent = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    xAxisGroup.appendChild(label);
  }
  svg.appendChild(xAxisGroup);
  
  // Draw series lines
  processedSeries.forEach((series, index) => {
    const color = SERIES_COLORS[index] || SERIES_COLORS[0];
    const style = SERIES_STYLES[index] || SERIES_STYLES[0];
    
    // Build path
    let pathD = '';
    series.processedData.forEach((d, i) => {
      const x = xScale(d.time);
      const y = yScale(d.value);
      pathD += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
    });
    
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', pathD);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', color.stroke);
    path.setAttribute('stroke-width', style.width);
    if (style.dash) {
      path.setAttribute('stroke-dasharray', style.dash);
    }
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
  });
  
  // Crosshair and tooltip (interactive layer)
  const interactiveGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  interactiveGroup.setAttribute('class', 'chart-interactive');
  
  // Crosshair line (hidden initially)
  const crosshair = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  crosshair.setAttribute('x1', 0);
  crosshair.setAttribute('y1', padding.top);
  crosshair.setAttribute('x2', 0);
  crosshair.setAttribute('y2', height - padding.bottom);
  crosshair.setAttribute('stroke', 'rgba(151, 184, 160, 0.4)');
  crosshair.setAttribute('stroke-width', '1');
  crosshair.setAttribute('stroke-dasharray', '4,2');
  crosshair.setAttribute('visibility', 'hidden');
  crosshair.setAttribute('class', 'chart-crosshair');
  interactiveGroup.appendChild(crosshair);
  
  // Invisible rect for mouse events
  const eventRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  eventRect.setAttribute('x', padding.left);
  eventRect.setAttribute('y', padding.top);
  eventRect.setAttribute('width', chartWidth);
  eventRect.setAttribute('height', chartHeight);
  eventRect.setAttribute('fill', 'transparent');
  eventRect.style.cursor = 'crosshair';
  interactiveGroup.appendChild(eventRect);
  
  svg.appendChild(interactiveGroup);
  container.appendChild(svg);
  
  // Tooltip element (HTML, outside SVG)
  const tooltip = el('div', { className: 'chart-mode__tooltip' }, []);
  tooltip.style.display = 'none';
  container.appendChild(tooltip);
  
  // Mouse event handlers
  function handleMouseMove(e) {
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;
    
    if (x < padding.left || x > width - padding.right) {
      crosshair.setAttribute('visibility', 'hidden');
      tooltip.style.display = 'none';
      return;
    }
    
    crosshair.setAttribute('x1', x);
    crosshair.setAttribute('x2', x);
    crosshair.setAttribute('visibility', 'visible');
    
    // Find time at cursor position
    const time = minTime + ((x - padding.left) / chartWidth) * (maxTime - minTime);
    const date = new Date(time * 1000);
    
    // Find values for each series at this time
    const tooltipContent = [
      el('div', { className: 'chart-tooltip__date' }, [
        date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      ]),
    ];
    
    processedSeries.forEach((series, index) => {
      // Find closest data point
      let closest = null;
      let closestDist = Infinity;
      series.processedData.forEach(d => {
        const dist = Math.abs(d.time - time);
        if (dist < closestDist) {
          closestDist = dist;
          closest = d;
        }
      });
      
      if (closest) {
        const color = SERIES_COLORS[index] || SERIES_COLORS[0];
        const displayValue = closest.originalValue !== undefined ? closest.originalValue : closest.value;
        tooltipContent.push(
          el('div', { className: 'chart-tooltip__row' }, [
            el('span', { 
              className: 'chart-tooltip__color',
              style: `background: ${color.stroke}`,
            }, []),
            el('span', { className: 'chart-tooltip__label' }, [series.signalId.replace(/_/g, ' ')]),
            el('span', { className: 'chart-tooltip__value' }, [formatValue(displayValue, series.signalId)]),
          ])
        );
      }
    });
    
    tooltip.innerHTML = '';
    tooltipContent.forEach(c => tooltip.appendChild(c));
    tooltip.style.display = 'block';
    
    // Position tooltip
    const tooltipRect = tooltip.getBoundingClientRect();
    let tooltipX = e.clientX - rect.left + 15;
    let tooltipY = e.clientY - rect.top - 10;
    
    // Keep tooltip in bounds
    if (tooltipX + tooltipRect.width > width - 10) {
      tooltipX = e.clientX - rect.left - tooltipRect.width - 15;
    }
    if (tooltipY < 10) tooltipY = 10;
    if (tooltipY + tooltipRect.height > height - 10) {
      tooltipY = height - tooltipRect.height - 10;
    }
    
    tooltip.style.left = tooltipX + 'px';
    tooltip.style.top = tooltipY + 'px';
  }
  
  function handleMouseLeave() {
    crosshair.setAttribute('visibility', 'hidden');
    tooltip.style.display = 'none';
  }
  
  eventRect.addEventListener('mousemove', handleMouseMove);
  eventRect.addEventListener('mouseleave', handleMouseLeave);
  
  return {
    svg,
    destroy: () => {
      eventRect.removeEventListener('mousemove', handleMouseMove);
      eventRect.removeEventListener('mouseleave', handleMouseLeave);
    },
  };
}

// ============================================================================
// Data Loading
// ============================================================================

async function loadSignalData(signalId, range) {
  const rangeConfig = TIME_RANGES.find(r => r.id === range) || TIME_RANGES[1];
  
  try {
    // Fetch signal metadata if not cached
    if (!chartModeState.signalData[signalId]) {
      const signalMeta = await fetchSignal(signalId);
      chartModeState.signalData[signalId] = signalMeta;
    }
    
    // Fetch history data
    const history = await fetchSignalHistory(signalId, { days: rangeConfig.days });
    const rawData = history.data || history || [];
    const chartData = toChartData(rawData);
    
    chartModeState.historyData[signalId] = chartData;
    
    return chartData;
  } catch (err) {
    console.error(`[ChartMode] Failed to load ${signalId}:`, err);
    throw err;
  }
}

async function refreshChart() {
  if (!containerRef) return;
  
  const chartContainer = containerRef.querySelector('.chart-mode__chart-area');
  if (!chartContainer) return;
  
  const { signals, range, mode, events } = chartModeState;
  
  if (signals.length === 0) {
    chartContainer.innerHTML = '<div class="chart-mode__empty">Select a signal from the sidebar to begin</div>';
    updateLegend([]);
    updateNarrative(null);
    return;
  }
  
  // Show loading
  chartContainer.innerHTML = '<div class="chart-mode__loading">Loading chart data...</div>';
  
  try {
    // Load data for all signals in parallel
    const dataPromises = signals.map(id => loadSignalData(id, range));
    await Promise.all(dataPromises);
    
    // Build series data for chart
    const seriesData = signals.map(signalId => ({
      signalId,
      data: chartModeState.historyData[signalId] || [],
      meta: chartModeState.signalData[signalId],
    }));
    
    // Render chart
    if (chartInstance?.destroy) {
      chartInstance.destroy();
    }
    chartInstance = renderChart(chartContainer, seriesData, { mode, showEvents: events });
    
    // Update legend
    updateLegend(seriesData);
    
    // Update URL
    updateChartURL(chartModeState);
    
    // Generate narrative for primary signal
    if (signals.length > 0) {
      const primarySignal = chartModeState.signalData[signals[0]];
      const primaryData = chartModeState.historyData[signals[0]];
      updateNarrative(primarySignal, primaryData);
    }
    
  } catch (err) {
    console.error('[ChartMode] Failed to refresh chart:', err);
    chartContainer.innerHTML = `<div class="chart-mode__error">Failed to load data: ${err.message}</div>`;
  }
}

function updateLegend(seriesData) {
  if (!containerRef) return;
  const legendContainer = containerRef.querySelector('.chart-mode__legend');
  if (!legendContainer) return;
  
  legendContainer.innerHTML = '';
  
  seriesData.forEach((series, index) => {
    const color = SERIES_COLORS[index] || SERIES_COLORS[0];
    const style = SERIES_STYLES[index] || SERIES_STYLES[0];
    const meta = series.meta || {};
    const currentValue = meta.current?.value;
    
    const legendItem = el('div', { className: 'chart-legend__item' }, [
      el('span', { 
        className: 'chart-legend__line',
        style: `background: ${color.stroke}; ${style.dash ? 'background: repeating-linear-gradient(90deg, ' + color.stroke + ' 0, ' + color.stroke + ' 4px, transparent 4px, transparent 8px)' : ''}`,
      }, []),
      el('span', { className: 'chart-legend__name' }, [series.signalId.replace(/_/g, ' ')]),
      el('span', { className: 'chart-legend__value' }, [formatValue(currentValue, series.signalId)]),
      el('button', { 
        className: 'chart-legend__remove',
        title: 'Remove signal',
        onclick: (e) => {
          e.stopPropagation();
          removeSignal(series.signalId);
        },
      }, ['×']),
    ]);
    
    legendContainer.appendChild(legendItem);
  });
}

async function updateNarrative(signal, chartData) {
  if (!containerRef) return;
  const narrativeContainer = containerRef.querySelector('.chart-mode__narrative');
  if (!narrativeContainer) return;
  
  if (!signal || !chartData || chartData.length === 0) {
    narrativeContainer.innerHTML = '';
    return;
  }
  
  // Show loading
  narrativeContainer.innerHTML = '';
  narrativeContainer.appendChild(
    el('div', { className: 'narrative-loading' }, [
      el('span', { className: 'narrative-loading__icon' }, ['🤖']),
      el('span', { className: 'narrative-loading__text' }, ['Analyzing...']),
    ])
  );
  
  try {
    const result = await generateNarrative(signal.signal_id, chartData, chartModeState.range, signal);
    
    narrativeContainer.innerHTML = '';
    if (result.text) {
      narrativeContainer.appendChild(
        el('div', { className: 'narrative-content' }, [
          el('div', { className: 'narrative-content__header' }, [
            el('span', { className: 'narrative-content__icon' }, ['🤖']),
            el('span', { className: 'narrative-content__label' }, ['AI Analysis']),
            result.cached ? el('span', { className: 'narrative-content__cached' }, ['cached']) : null,
          ].filter(Boolean)),
          el('p', { className: 'narrative-content__text' }, [result.text]),
        ])
      );
    }
  } catch (err) {
    console.error('[ChartMode] Narrative failed:', err);
    narrativeContainer.innerHTML = '';
  }
}

// ============================================================================
// Signal Management
// ============================================================================

export function addSignal(signalId) {
  if (chartModeState.signals.includes(signalId)) return;
  if (chartModeState.signals.length >= 4) {
    console.warn('[ChartMode] Maximum 4 signals allowed');
    return;
  }
  
  chartModeState.signals.push(signalId);
  updateSidebarSignals(chartModeState.signals);
  refreshChart();
}

export function removeSignal(signalId) {
  const index = chartModeState.signals.indexOf(signalId);
  if (index === -1) return;
  
  chartModeState.signals.splice(index, 1);
  updateSidebarSignals(chartModeState.signals);
  refreshChart();
}

export function setRange(rangeId) {
  if (chartModeState.range === rangeId) return;
  chartModeState.range = rangeId;
  
  // Update button states
  if (containerRef) {
    containerRef.querySelectorAll('.range-btn').forEach(btn => {
      btn.classList.toggle('range-btn--active', btn.dataset.range === rangeId);
    });
  }
  
  refreshChart();
}

export function setDisplayMode(modeId) {
  if (chartModeState.mode === modeId) return;
  chartModeState.mode = modeId;
  
  // Update button states
  if (containerRef) {
    containerRef.querySelectorAll('.mode-btn').forEach(btn => {
      btn.classList.toggle('mode-btn--active', btn.dataset.mode === modeId);
    });
  }
  
  refreshChart();
}

export function toggleEvents() {
  chartModeState.events = !chartModeState.events;
  refreshChart();
}

// ============================================================================
// Component
// ============================================================================

export function ChartMode(config = {}) {
  // Initialize state from config
  chartModeState = {
    signals: config.signals || [],
    signalData: {},
    historyData: {},
    range: config.range || '90d',
    mode: config.mode || 'normalized',
    events: config.events !== false,
    loading: false,
    error: null,
  };
  
  // Time range buttons
  const timeRangeButtons = TIME_RANGES.map(range => 
    el('button', {
      className: `range-btn ${range.id === chartModeState.range ? 'range-btn--active' : ''}`,
      'data-range': range.id,
      onclick: () => setRange(range.id),
    }, [range.label])
  );
  
  // Display mode buttons
  const displayModeButtons = DISPLAY_MODES.map(mode =>
    el('button', {
      className: `mode-btn ${mode.id === chartModeState.mode ? 'mode-btn--active' : ''}`,
      'data-mode': mode.id,
      title: mode.desc,
      onclick: () => setDisplayMode(mode.id),
    }, [mode.label])
  );
  
  // Export handler
  async function handleExport(format) {
    const chartArea = containerRef?.querySelector('.chart-mode__chart-area');
    const svgElement = chartArea?.querySelector('svg');
    const primarySignal = chartModeState.signalData[chartModeState.signals[0]];
    const primaryData = chartModeState.historyData[chartModeState.signals[0]];
    
    if (!svgElement || !primarySignal) {
      throw new Error('Chart not ready');
    }
    
    switch (format) {
      case 'png':
        await exportPNG(svgElement, primarySignal, chartModeState.range);
        break;
      case 'csv':
        exportCSV(primaryData, primarySignal, chartModeState.range);
        break;
      case 'json':
        exportJSON(primaryData, primarySignal, chartModeState.range, null, null);
        break;
      case 'pdf':
        await exportPDF(svgElement, primaryData, primarySignal, chartModeState.range, null, null);
        break;
    }
  }
  
  const exportDropdown = createExportDropdown(handleExport);
  
  // Close handler
  function handleClose() {
    // Navigate back - check if there's history, otherwise go to signals
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.hash = '/signals';
    }
  }
  
  // Build component
  const component = el('div', { className: 'chart-mode' }, [
    // Header
    el('header', { className: 'chart-mode__header' }, [
      el('div', { className: 'chart-mode__header-left' }, [
        el('a', { href: '#/signals', className: 'chart-mode__logo' }, ['CAPITULATORS.IO']),
        el('span', { className: 'chart-mode__title' }, ['Chart Mode']),
      ]),
      el('div', { className: 'chart-mode__header-right' }, [
        exportDropdown,
        el('button', { 
          className: 'chart-mode__close',
          title: 'Close Chart Mode',
          onclick: handleClose,
        }, ['×']),
      ]),
    ]),
    
    // Main content area
    el('div', { className: 'chart-mode__body' }, [
      // Chart area
      el('div', { className: 'chart-mode__main' }, [
        // Legend
        el('div', { className: 'chart-mode__legend' }, []),
        
        // Chart container
        el('div', { className: 'chart-mode__chart-area' }, [
          el('div', { className: 'chart-mode__empty' }, ['Select a signal from the sidebar to begin']),
        ]),
        
        // Controls bar
        el('div', { className: 'chart-mode__controls' }, [
          el('div', { className: 'chart-mode__time-range' }, timeRangeButtons),
          el('div', { className: 'chart-mode__display-mode' }, displayModeButtons),
        ]),
        
        // Narrative
        el('div', { className: 'chart-mode__narrative' }, []),
      ]),
      
      // Sidebar
      ChartModeSidebar({
        signals: chartModeState.signals,
        onAddSignal: addSignal,
        onRemoveSignal: removeSignal,
      }),
    ]),
  ]);
  
  containerRef = component;
  
  return component;
}

/**
 * Initialize chart mode after DOM is ready
 */
export function initChartMode(config) {
  // If signals were provided, load them
  if (chartModeState.signals.length > 0) {
    refreshChart();
  }
}

/**
 * Cleanup chart mode
 */
export function cleanupChartMode() {
  if (chartInstance?.destroy) {
    chartInstance.destroy();
    chartInstance = null;
  }
  containerRef = null;
  chartModeState = {
    signals: [],
    signalData: {},
    historyData: {},
    range: '90d',
    mode: 'normalized',
    events: true,
    loading: false,
    error: null,
  };
}

/**
 * Get current chart mode state
 */
export function getChartModeState() {
  return { ...chartModeState };
}
